import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, Trash2, Pin, FileText, Image as ImageIcon, X, Edit2 } from "lucide-react";
import LinkifyText from "../components/LinkifyText";

/**
 * Admin-only CRUD over lodge-scoped announcements. Announcements land
 * instantly on every brother's mobile in Lodge → Anunțuri tab and fire
 * a bell notification. Members cannot post here.
 */
export default function AnnouncementsPage() {
  const { t, i18n } = useTranslation();
  const locale =t("announcements.en_us");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", pinned: false, photo_url: null, document_urls: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/lodge-announcements");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", body: "", pinned: false, photo_url: null, document_urls: [] });
    setShowForm(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title || "", body: a.body || "", pinned: !!a.pinned,
      photo_url: a.photo_url || null, document_urls: a.document_urls || [],
    });
    setShowForm(true);
  };

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/lodge-announcements/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, photo_url: data.path }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const onPickDoc = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/lodge-announcements/upload-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, document_urls: [...prev.document_urls, { filename: data.filename, path: data.path }] }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error(t("announcements.titleRequired")); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/lodge-announcements/${editing.id}`, {
          title: form.title, body: form.body, pinned: form.pinned,
        });
        toast.success(t("announcements.updated"));
      } else {
        await api.post("/lodge-announcements", form);
        toast.success(t("announcements.posted"));
      }
      setShowForm(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(t("announcements.deleteConfirm", { title: a.title }))) return;
    try {
      await api.delete(`/lodge-announcements/${a.id}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const togglePin = async (a) => {
    try {
      await api.patch(`/lodge-announcements/${a.id}`, { pinned: !a.pinned });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="container-lodge">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
        <div>
          <h2 className="page-title">{t("announcements.title")}</h2>
          <p className="kv-label">{t("announcements.subtitle")}</p>
        </div>
        {isAdmin && (
          <button className="btn-gold" onClick={openNew} data-testid="announcement-new-btn">
            <Plus size={14} /> {t("announcements.new")}
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="panel-lodge" style={{ padding: "2rem", textAlign: "center", color: "#6b6557", fontStyle: "italic" }}>
          {t("announcements.empty")}
        </div>
      ) : (
        <div style={{ display: "grid", gap: "0.75rem" }}>
          {rows.map((a) => (
            <div key={a.id} className="panel-lodge" style={{ padding: "1rem", borderLeft: a.pinned ? "3px solid #c9a84c" : undefined }} data-testid={`announcement-${a.id}`}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.5rem" }}>
                <div style={{ flex: 1 }}>
                  {a.pinned ? (
                    <span style={{ fontSize: "0.65rem", color: "#9a7530", fontWeight: 700, letterSpacing: 1 }}>
                      <Pin size={10} style={{ display: "inline", marginRight: 3 }} />{t("announcements.pinned")}
                    </span>
                  ) : null}
                  <h3 style={{ margin: "0.2rem 0", color: "#1f1b10" }}>{a.title}</h3>
                  <div style={{ fontSize: "0.72rem", color: "#6b6557" }}>
                    {new Date(a.created_at).toLocaleString(locale)} · {a.created_by}
                  </div>
                </div>
                {isAdmin && (
                  <div style={{ display: "flex", gap: 4 }}>
                    <button className="btn-outline-gold" onClick={() => togglePin(a)} title={a.pinned ? t("announcements.unpin") : t("announcements.pin")} data-testid={`announcement-pin-${a.id}`}>
                      <Pin size={12} />
                    </button>
                    <button className="btn-outline-gold" onClick={() => openEdit(a)} data-testid={`announcement-edit-${a.id}`}>
                      <Edit2 size={12} />
                    </button>
                    <button className="btn-danger" onClick={() => remove(a)} data-testid={`announcement-delete-${a.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
              {a.body ? (
                <p style={{ margin: "0.5rem 0 0", color: "#34322d", lineHeight: 1.5, whiteSpace: "pre-wrap", userSelect: "text" }}>
                  <LinkifyText text={a.body} />
                </p>
              ) : null}
              {a.document_urls?.length ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: "0.5rem" }}>
                  {a.document_urls.map((d) => (
                    <a
                      key={d.path}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/announcements/document?path=${encodeURIComponent(d.path)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="badge-grade"
                      style={{ fontSize: "0.65rem", textDecoration: "none" }}
                      data-testid={`announcement-doc-${d.path.split("/").pop()}`}
                    >
                      <FileText size={10} style={{ display: "inline", marginRight: 3 }} />{d.filename}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="dialog-overlay" onClick={() => setShowForm(false)}>
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
              <h3 style={{ margin: 0 }}>{editing ? t("announcements.editTitle") : t("announcements.newTitle")}</h3>
              <button className="btn-outline-gold" onClick={() => setShowForm(false)}><X size={14} /></button>
            </div>
            <label className="kv-label">{t("announcements.titleLabel")}</label>
            <input className="input-lodge" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="announcement-title-input" />
            <label className="kv-label" style={{ marginTop: "0.5rem" }}>{t("announcements.bodyLabel")}</label>
            <textarea className="input-lodge" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="announcement-body-input" />

            {!editing && (
              <>
                <div style={{ display: "flex", gap: 6, marginTop: "0.75rem" }}>
                  <label className="btn-outline-gold" style={{ cursor: "pointer" }}>
                    <ImageIcon size={12} /> {t("announcements.photo")}
                    <input type="file" accept="image/*" onChange={onPickPhoto} style={{ display: "none" }} />
                  </label>
                  <label className="btn-outline-gold" style={{ cursor: "pointer" }}>
                    <FileText size={12} /> {t("announcements.document")}
                    <input type="file" accept=".pdf,.doc,.docx" onChange={onPickDoc} style={{ display: "none" }} />
                  </label>
                </div>
                {form.photo_url ? <div style={{ fontSize: "0.72rem", color: "#9a7530", marginTop: 4 }}><ImageIcon size={10} style={{ display: "inline" }} /> {t("announcements.photoAttached")}</div> : null}
                {form.document_urls.map((d) => (
                  <div key={d.path} style={{ fontSize: "0.72rem", color: "#9a7530", marginTop: 4 }}>
                    <FileText size={10} style={{ display: "inline" }} /> {d.filename}
                  </div>
                ))}
              </>
            )}

            <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: "0.75rem", fontSize: "0.85rem" }}>
              <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} />
              <Pin size={12} /> {t("announcements.pinToTop")}
            </label>

            <div style={{ display: "flex", gap: 6, marginTop: "1rem", justifyContent: "flex-end" }}>
              <button className="btn-outline-gold" onClick={() => setShowForm(false)}>{t("announcements.cancel")}</button>
              <button className="btn-gold" onClick={save} disabled={busy} data-testid="announcement-save-btn">
                {busy ? "..." : (editing ? t("announcements.save") : t("announcements.publish"))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
